import React, { useCallback, useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import {
  isDashboardPendingResponse,
  type DashboardInvoiceActionItem,
  type DashboardTodayApiResponse,
} from '@/lib/api/dashboard';
import {
  deleteClientInvoice,
  getClientInvoice,
  getClientInvoices,
  type InvoiceDateBasis,
  Invoice,
  resendInvoiceEmail,
  unarchiveClientInvoice,
  updateInvoiceStatus,
} from '@/lib/api/invoices';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  ListItemText,
  Link,
  Menu,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowUpRight, ChevronDown, Download, EllipsisVertical, X } from 'lucide-react';
import CreateInvoiceFlowDialog from '@/components/dashboard/CreateInvoiceFlowDialog';
import InvoiceDetailsDialog from '@/components/dashboard/InvoiceDetailsDialog';
import NotificationCenter from '@/components/notifications/NotificationCenter';
import totalInvoicedIcon from '@/assets/images/invoice-total-invoiced.svg';
import amountCollectedIcon from '@/assets/images/invoice-amount-collected.svg';
import overdueIcon from '@/assets/images/invoice-overdue.svg';
import invoiceDocumentIcon from '@/assets/images/invoice-document.svg';
import invoiceArchiveIcon from '@/assets/images/invoice-download.svg';
import invoiceDeleteIcon from '@/assets/images/trash-bin-trash.svg';
import appointmentCalendarFilterIcon from '@/assets/images/appointments/appointment-calendar-filter.svg';
import SlashSearchInput from '@/components/shared/SlashSearchInput';
import PaginationControls from '@/components/shared/PaginationControls';
import { toast } from 'sonner';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InvoiceConfirmDialog, { type InvoiceConfirmVariant, type InvoiceDialogFeedback } from '@/components/dashboard/InvoiceConfirmDialog';
import InvoiceStatusConfirmDialog from '@/components/dashboard/InvoiceStatusConfirmDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuth } from '@/hooks/useAuth';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { downloadInvoicePdf, getInvoicePdfFallbackFromProfile, invoiceApiToPdfPayload, withInvoiceClinicLogoDataUrl } from '@/utils/invoicePdfExport';
import { DateRange } from 'react-day-picker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Tag } from '@/components/ui/tag';
import {
  formatInvoiceCurrencyAmount,
  getInvoiceCurrencyBadgeLabel,
  getInvoiceListDisplayCurrency,
  normalizeInvoiceCurrency,
  readInvoiceCurrencyFromResponse,
} from '@/utils/invoiceCurrency';

const invoiceTableColumns = '1.2fr 1fr 1fr 1fr 1fr 1fr 1.2fr 0.5fr';
const INVOICE_LIST_PAGE_SIZE = 20;

/** Figma: Invoice-Flow---New — node 4:362 (billing panel) */
const invoicingFont = { fontFamily: '"Inter", system-ui, sans-serif' } as const;
const invoiceBodyTextSx = {
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '18px',
  color: '#1a1a1a',
} as const;

/** KPI amounts: Total Invoiced / Collected / Overdue */
const invoicingSummaryAmountSx = {
  color: '#000000',
  fontFamily: '"Inter", system-ui, sans-serif',
  fontSize: '24px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '140%',
} as const;

/** KPI captions: e.g. Total Invoiced, This month */
const invoicingSummaryCaptionSx = {
  color: '#808080',
  fontFamily: 'Arial, sans-serif',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '140%',
  alignSelf: 'stretch',
} as const;

const tabCountChipSx = {
  height: 20,
  minWidth: 28,
  fontSize: '12px',
  lineHeight: '16px',
  display: 'inline-flex',
  alignItems: 'center',
} as const;

const invoiceTableCellSx = {
  px: { xs: 1.8, md: 2.4 },
  py: { xs: 2.4, md: 4.15 },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minWidth: 0,
} as const;

type InvoiceActionKey = 'view' | 'archive' | 'delete' | 'download';
type StatusSelectValue = 'due' | 'overdue' | 'paid' | 'discarded';
const LIST_MARK_PAID_PAYMENT_METHOD = 'upi';

const getApiErrorMessage = (error: unknown): string => {
  const maybeObj = error as { response?: { data?: { error?: string; message?: string } }; message?: string };
  return maybeObj?.response?.data?.error || maybeObj?.response?.data?.message || maybeObj?.message || 'Could not update invoice status';
};

const normalizeStatusForSelect = (status?: string): StatusSelectValue => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'discarded' || s === 'archived') return 'discarded';
  if (s === 'overdue') return 'overdue';
  return 'due';
};

const mapUiStatusToApiStatus = (value: StatusSelectValue): string => (value === 'due' ? 'sent' : value);

/** Deep-link from dashboard etc.: `/invoicing?tab=due` → list tab index. */
const INVOICE_LIST_TAB_QUERY_MAP: Record<string, number> = {
  all: 0,
  paid: 1,
  due: 2,
  archived: 3,
};

const normalizeInvoiceDateBasis = (value?: string | null): InvoiceDateBasis => {
  const normalized = value?.trim().toLowerCase().replace(/[\s_-]/g, '');
  return normalized === 'paiddate' || normalized === 'paidat' || normalized === 'paid'
    ? 'paidDate'
    : 'invoiceDate';
};

const isInvoiceArchivedOrDiscarded = (inv: Invoice | null | undefined): boolean => {
  const s = (inv?.status || '').toLowerCase();
  return s === 'archived' || s === 'discarded';
};

const invoiceMatchesActiveTab = (inv: Invoice, activeTab: number): boolean => {
  const status = (inv.status || '').toLowerCase();
  if (activeTab === 1) return status === 'paid';
  if (activeTab === 2) return ['due', 'unpaid', 'overdue', 'sent'].includes(status);
  if (activeTab === 3) return isInvoiceArchivedOrDiscarded(inv);
  return true;
};

const normalizeDashboardInvoiceStatus = (status?: string | null): 'due' | 'overdue' | 'remove' => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'overdue') return 'overdue';
  if (normalized === 'due' || normalized === 'sent' || normalized === 'unpaid') return 'due';
  return 'remove';
};

const getDashboardInvoiceCounts = (items: DashboardInvoiceActionItem[]) => {
  const overdueCount = items.filter((item) => normalizeDashboardInvoiceStatus(item.status || item.invoiceStatus) === 'overdue').length;
  const dueCount = items.filter((item) => normalizeDashboardInvoiceStatus(item.status || item.invoiceStatus) === 'due').length;
  return { overdueCount, dueCount };
};

const statusLabel = (value: StatusSelectValue): string => {
  if (value === 'overdue') return 'Overdue';
  if (value === 'paid') return 'Paid';
  if (value === 'discarded') return 'Discarded';
  return 'Due';
};

const statusColor = (value: StatusSelectValue): string => {
  if (value === 'paid') return '#28c76f';
  if (value === 'overdue') return '#ea5455';
  if (value === 'discarded') return '#64748b';
  return '#ea5455';
};

/** Light blue — matches invoice UI accents / Figma download control */
const INVOICE_MENU_DOWNLOAD_ICON_COLOR = '#57b7de';

const invoiceActionItems: Array<{
  key: InvoiceActionKey;
  label: string;
  iconSrc?: string;
  iconAlt: string;
  iconSize: number;
}> = [
  { key: 'view', label: 'View Invoice', iconSrc: invoiceDocumentIcon, iconAlt: 'View invoice', iconSize: 18 },
  { key: 'archive', label: 'Archive', iconSrc: invoiceArchiveIcon, iconAlt: 'Archive invoice', iconSize: 18 },
  { key: 'delete', label: 'Delete', iconSrc: invoiceDeleteIcon, iconAlt: 'Delete invoice', iconSize: 20 },
  { key: 'download', label: 'Download', iconAlt: 'Download invoice PDF', iconSize: 18 },
];

const InvoicingPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get('tab')?.trim().toLowerCase();
    if (!raw) return 0;
    return INVOICE_LIST_TAB_QUERY_MAP[raw] ?? 0;
  });
  const [isCreateInvoiceOpen, setIsCreateInvoiceOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [activeActionRowIndex, setActiveActionRowIndex] = useState<number | null>(null);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [remindingInvoiceId, setRemindingInvoiceId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [invoiceDateBasis, setInvoiceDateBasis] = useState<InvoiceDateBasis>(() =>
    normalizeInvoiceDateBasis(new URLSearchParams(window.location.search).get('dateBasis'))
  );
  const [statusCounts, setStatusCounts] = useState<{ all: number; paid: number; due: number; archived: number } | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | null>(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<string | null>(null);
  const [pdfDownloadingId, setPdfDownloadingId] = useState<string | null>(null);
  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ invoice: Invoice; next: StatusSelectValue } | null>(null);
  const [pendingInvoiceConfirm, setPendingInvoiceConfirm] = useState<{
    variant: InvoiceConfirmVariant;
    invoice: Invoice;
  } | null>(null);
  const [invoiceStatusFeedback, setInvoiceStatusFeedback] = useState<InvoiceDialogFeedback | null>(null);
  const [invoiceConfirmFeedback, setInvoiceConfirmFeedback] = useState<InvoiceDialogFeedback | null>(null);
  const [summary, setSummary] = useState({
    totalBilled: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    paidCount: 0,
    overdueCount: 0,
  });
  const [summaryCurrency, setSummaryCurrency] = useState('');
  const [invoiceListPage, setInvoiceListPage] = useState(1);
  const [invoiceListTotalPages, setInvoiceListTotalPages] = useState(1);
  const nutritionistCurrency = normalizeInvoiceCurrency(user?.currency);

  useEffect(() => {
    setInvoiceListPage(1);
  }, [sortOrder, debouncedSearch, dateRange, invoiceDateBasis]);

  useEffect(() => {
    const raw = searchParams.get('tab')?.trim().toLowerCase();
    const next = raw ? INVOICE_LIST_TAB_QUERY_MAP[raw] : undefined;
    if (typeof next === 'number') {
      setActiveTab(next);
    }
    const rawDateBasis = searchParams.get('dateBasis');
    if (rawDateBasis) {
      setInvoiceDateBasis(normalizeInvoiceDateBasis(rawDateBasis));
    }
  }, [searchParams]);

  const loadInvoices = useCallback(async () => {
    try {
      setIsLoading(true);
      const statusParam = activeTab === 1 ? 'paid' : activeTab === 2 ? 'due' : activeTab === 3 ? 'archived' : undefined;
      
      const fromDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;
      const toDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;

      const data = await getClientInvoices({
        status: statusParam,
        sort: sortOrder,
        page: invoiceListPage,
        limit: INVOICE_LIST_PAGE_SIZE,
        search: debouncedSearch.trim() || undefined,
        fromDate,
        toDate: toDate || fromDate, // If only one date is selected, fromDate and toDate can be the same, or we just pass toDate if exists
        currency: nutritionistCurrency || undefined,
        dateBasis: invoiceDateBasis,
      });
      const fetchedInvoices = Array.isArray(data?.invoices) ? data.invoices : (Array.isArray(data) ? data : []);
      setInvoices(fetchedInvoices);
      setSummaryCurrency(
        readInvoiceCurrencyFromResponse(data) ||
        nutritionistCurrency ||
        getInvoiceListDisplayCurrency(fetchedInvoices)
      );
      const pagesRaw = Number((data as { pagination?: { totalPages?: number } })?.pagination?.totalPages);
      setInvoiceListTotalPages(Number.isFinite(pagesRaw) && pagesRaw >= 1 ? pagesRaw : 1);
      if (data?.countsByStatus) {
        setStatusCounts({
          all: Number(data.countsByStatus.all) || 0,
          paid: Number(data.countsByStatus.paid) || 0,
          due: Number(data.countsByStatus.due) || 0,
          archived: Number(data.countsByStatus.archived) || 0,
        });
      } else {
        setStatusCounts(null);
      }

      let totalBilled = 0;
      let totalPaid = 0;
      let totalOutstanding = 0;
      let paidCount = 0;
      let overdueCount = 0;

      if (data?.summary) {
        const hasDateFilter = Boolean(fromDate || toDate);
        totalBilled = hasDateFilter
          ? data.summary.totalBilled ?? data.summary.totalInvoicedThisMonth ?? 0
          : data.summary.totalInvoicedThisMonth ?? data.summary.totalBilled ?? 0;
        totalPaid = data.summary.collected?.amount ?? data.summary.totalPaid ?? 0;
        totalOutstanding = data.summary.totalDue ?? data.summary.totalOutstanding ?? 0;
        paidCount = data.summary.collected?.invoiceCount ?? 0;
        overdueCount = data.summary.overdueCount ?? 0;
      } else {
        fetchedInvoices.forEach(inv => {
          const amount = Number(inv.totalDue) || Number(inv.amount) || 0;
          totalBilled += amount;

          const status = (inv.status || '').toLowerCase();
          if (status === 'paid') {
            totalPaid += amount;
          } else if (status === 'due' || status === 'overdue' || status === 'unpaid' || status === 'sent') {
            totalOutstanding += amount;
          }
        });
      }

      if (paidCount === 0) {
        paidCount = fetchedInvoices.filter(inv => (inv.status || '').toLowerCase() === 'paid').length;
      }

      if (overdueCount === 0) {
        const now = new Date();
        overdueCount = fetchedInvoices.filter(inv => {
          const status = (inv.status || '').toLowerCase();
          if (status === 'paid') return false;
          if (!inv.dueDate) return false;
          const due = new Date(inv.dueDate);
          return due < now;
        }).length;
      }

      setSummary({ totalBilled, totalPaid, totalOutstanding, paidCount, overdueCount });
    } catch (error) {
      console.error('Failed to fetch invoices', error);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, sortOrder, debouncedSearch, dateRange, invoiceListPage, invoiceDateBasis, nutritionistCurrency]);

  useEffect(() => {
    void loadInvoices();
  }, [loadInvoices]);

  const invoiceDisplayCurrency = getInvoiceListDisplayCurrency(invoices, summaryCurrency || nutritionistCurrency);
  const invoiceCurrencyBadgeLabel = getInvoiceCurrencyBadgeLabel(invoiceDisplayCurrency);
  const invoiceDateColumnLabel = invoiceDateBasis === 'paidDate' ? 'Paid Date' : 'Billed Date';
  const invoiceSummaryDateCaption = dateRange?.from
    ? invoiceDateBasis === 'paidDate'
      ? 'Selected paid dates'
      : 'Selected invoice dates'
    : invoiceDateBasis === 'paidDate'
      ? 'This month paid'
      : 'This month';
  const getInvoiceBasisDate = (invoice: Invoice) =>
    invoiceDateBasis === 'paidDate' ? invoice.paidAt : invoice.issueDate || invoice.createdAt;

  const updateDashboardInvoiceCache = useCallback((invoiceId: string, status?: string) => {
    const dashboardStatus = normalizeDashboardInvoiceStatus(status);

    queryClient.setQueriesData<DashboardTodayApiResponse>(
      { queryKey: ['dashboard', 'today'] },
      (current) => {
        if (!current || isDashboardPendingResponse(current) || !('invoicesAndBilling' in current)) {
          return current;
        }

        let changed = false;
        const nextItems = current.invoicesAndBilling.items.reduce<DashboardInvoiceActionItem[]>((items, item) => {
          if (String(item.id) !== invoiceId) {
            items.push(item);
            return items;
          }

          changed = true;
          if (dashboardStatus !== 'remove') {
            items.push({
              ...item,
              status: dashboardStatus,
              invoiceStatus: status || dashboardStatus,
            });
          }
          return items;
        }, []);

        if (!changed) {
          return current;
        }

        const { overdueCount, dueCount } = getDashboardInvoiceCounts(nextItems);
        return {
          ...current,
          cards: {
            ...current.cards,
            invoicesAndBilling: {
              ...current.cards.invoicesAndBilling,
              count: nextItems.length,
              overdueCount,
              dueCount,
            },
          },
          invoicesAndBilling: {
            ...current.invoicesAndBilling,
            counts: {
              overdue: overdueCount,
              due: dueCount,
            },
            items: nextItems,
          },
        };
      }
    );
  }, [queryClient]);

  const refreshDashboardQueries = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    await queryClient.refetchQueries({ queryKey: ['dashboard'], type: 'active' });

    const refetchDashboardAfterCacheJob = () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.refetchQueries({ queryKey: ['dashboard'], type: 'active' });
    };
    window.setTimeout(refetchDashboardAfterCacheJob, 2500);
    window.setTimeout(refetchDashboardAfterCacheJob, 8000);
  }, [queryClient]);

  const reloadInvoicesAndDashboard = useCallback(async () => {
    await Promise.allSettled([
      loadInvoices(),
      refreshDashboardQueries(),
    ]);
  }, [loadInvoices, refreshDashboardQueries]);

  const mergeInvoiceIntoList = useCallback((updatedInvoice: Invoice, fallbackStatus?: string) => {
    setInvoices((currentInvoices) => currentInvoices.reduce<Invoice[]>((nextInvoices, currentInvoice) => {
      if (currentInvoice.id !== updatedInvoice.id) {
        nextInvoices.push(currentInvoice);
        return nextInvoices;
      }

      const mergedInvoice: Invoice = {
        ...currentInvoice,
        ...updatedInvoice,
        status: updatedInvoice.status || fallbackStatus || currentInvoice.status,
      };

      if (invoiceMatchesActiveTab(mergedInvoice, activeTab)) {
        nextInvoices.push(mergedInvoice);
      }
      return nextInvoices;
    }, []));
  }, [activeTab]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MM-yyyy');
    } catch {
      return dateString;
    }
  };

  const getClientName = (inv: Invoice) => {
    if (inv.clientSnapshot?.name) {
      return String(inv.clientSnapshot.name).trim();
    }
    if (inv.clientSnapshot?.firstName || inv.clientSnapshot?.lastName) {
      return `${inv.clientSnapshot.firstName || ''} ${inv.clientSnapshot.lastName || ''}`.trim();
    }
    return 'Unknown Client';
  };

  const tabCounts = {
    0: statusCounts?.all ?? invoices.length,
    1: statusCounts?.paid ?? invoices.filter(inv => (inv.status || '').toLowerCase() === 'paid').length,
    2: statusCounts?.due ?? invoices.filter(inv => {
      const s = (inv.status || '').toLowerCase();
      return s === 'due' || s === 'unpaid' || s === 'overdue' || s === 'sent';
    }).length,
    3: statusCounts?.archived ?? invoices.filter(inv => {
      const s = (inv.status || '').toLowerCase();
      return s === 'discarded' || s === 'archived';
    }).length,
  };

  const tabLabels = [
    { label: 'All', count: tabCounts[0] },
    { label: 'Paid', count: tabCounts[1] },
    { label: 'Due', count: tabCounts[2] },
    { label: 'Archived', count: tabCounts[3] },
  ];

  const isActionsMenuOpen = Boolean(actionsAnchorEl);
  const menuTargetInvoice =
    activeActionRowIndex !== null && activeActionRowIndex >= 0 ? invoices[activeActionRowIndex] : undefined;

  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>, rowIndex: number) => {
    setActionsAnchorEl(event.currentTarget);
    setActiveActionRowIndex(rowIndex);
  };

  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
    setActiveActionRowIndex(null);
  };

  const handleSendReminder = async (invoiceId: string) => {
    if (remindingInvoiceId) return;
    setRemindingInvoiceId(invoiceId);
    try {
      await resendInvoiceEmail(invoiceId);
      toast.success('Reminder email sent');
    } catch (error) {
      console.error('Failed to send invoice reminder', error);
      toast.error('Could not send reminder. Please try again.');
    } finally {
      setRemindingInvoiceId(null);
    }
  };

  const handleStatusChange = (invoice: Invoice, event: SelectChangeEvent<StatusSelectValue>) => {
    const next = event.target.value as StatusSelectValue;
    const current = normalizeStatusForSelect(invoice.status);
    if (next === current) return;
    if (next === 'paid' || next === 'discarded') {
      setInvoiceStatusFeedback(null);
      setPendingStatusChange({ invoice, next });
      return;
    }
    void handleConfirmStatusChange(invoice, next);
  };

  const handleConfirmStatusChange = async (invoice: Invoice, next: StatusSelectValue) => {
    if (!invoice.id) {
      if (pendingStatusChange) {
        setInvoiceStatusFeedback({
          variant: 'error',
          title: 'Unable to update invoice status',
          message: 'Select a valid invoice before confirming this status change.',
        });
      }
      return;
    }
    setStatusUpdatingId(invoice.id);
    try {
      const fallbackStatus = mapUiStatusToApiStatus(next);
      const updatedInvoice = await updateInvoiceStatus(
        invoice.id,
        next === 'paid'
          ? {
              status: 'paid',
              paidAt: new Date().toISOString(),
              paymentMethod: LIST_MARK_PAID_PAYMENT_METHOD,
            }
          : { status: fallbackStatus }
      );
      mergeInvoiceIntoList(updatedInvoice, fallbackStatus);
      updateDashboardInvoiceCache(invoice.id, updatedInvoice.status || fallbackStatus);
      toast.success('Invoice status updated');
      setInvoiceStatusFeedback(null);
      setPendingStatusChange(null);
      await reloadInvoicesAndDashboard();
    } catch (error) {
      console.error('Failed to update invoice status', error);
      if (pendingStatusChange?.invoice?.id === invoice.id) {
        setInvoiceStatusFeedback({
          variant: 'error',
          title: 'Could not update invoice status',
          message: getApiErrorMessage(error),
        });
      } else {
        toast.error(getApiErrorMessage(error));
      }
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleInvoiceConfirmDialogConfirm = () => {
    const pending = pendingInvoiceConfirm;
    if (!pending?.invoice?.id) {
      setInvoiceConfirmFeedback({
        variant: 'error',
        title: 'Unable to update invoice',
        message: 'Select a valid invoice before confirming this action.',
      });
      return;
    }
    const { variant, invoice } = pending;

    if (variant === 'delete') {
      void (async () => {
        setDeletingInvoiceId(invoice.id);
        try {
          await deleteClientInvoice(invoice.id);
          setInvoices((currentInvoices) => currentInvoices.filter((currentInvoice) => currentInvoice.id !== invoice.id));
          updateDashboardInvoiceCache(invoice.id, 'deleted');
          toast.success('Invoice deleted');
          setInvoiceConfirmFeedback(null);
          setPendingInvoiceConfirm(null);
          await reloadInvoicesAndDashboard();
        } catch (error) {
          console.error('Failed to delete invoice', error);
          setInvoiceConfirmFeedback({
            variant: 'error',
            title: 'Could not delete invoice',
            message: getApiErrorMessage(error),
          });
        } finally {
          setDeletingInvoiceId(null);
        }
      })();
      return;
    }

    const restoring = variant === 'unarchive';
    void (async () => {
      setStatusUpdatingId(invoice.id);
      try {
        let updatedInvoice: Invoice;
        if (restoring) {
          updatedInvoice = await unarchiveClientInvoice(invoice.id);
        } else {
          updatedInvoice = await updateInvoiceStatus(invoice.id, { status: 'archived' });
        }
        mergeInvoiceIntoList(updatedInvoice, restoring ? 'sent' : 'archived');
        updateDashboardInvoiceCache(invoice.id, updatedInvoice.status || (restoring ? 'sent' : 'archived'));
        toast.success(restoring ? 'Invoice unarchived' : 'Invoice archived');
        setInvoiceConfirmFeedback(null);
        setPendingInvoiceConfirm(null);
        await reloadInvoicesAndDashboard();
      } catch (error) {
        console.error(restoring ? 'Failed to unarchive invoice' : 'Failed to archive invoice', error);
        setInvoiceConfirmFeedback({
          variant: 'error',
          title: restoring ? 'Could not unarchive invoice' : 'Could not archive invoice',
          message: getApiErrorMessage(error),
        });
      } finally {
        setStatusUpdatingId(null);
      }
    })();
  };

  const handleInvoiceActionClick = (action: InvoiceActionKey) => {
    if (activeActionRowIndex === null) {
      handleCloseActionsMenu();
      return;
    }

    const rowIndex = activeActionRowIndex;
    const invoice = invoices[rowIndex];
    handleCloseActionsMenu();

    if (action === 'view') {
      if (!invoice) return;
      setViewInvoice(invoice);
      return;
    }

    if (action === 'download') {
      if (!invoice?.id) return;
      void (async () => {
        setPdfDownloadingId(invoice.id);
        try {
          const full = await getClientInvoice(invoice.id);
          const fallbackFrom = await getInvoicePdfFallbackFromProfile({
            name: user?.fullName,
            email: user?.email,
          });
          const payload = await withInvoiceClinicLogoDataUrl(
            full.id || invoice.id,
            invoiceApiToPdfPayload(full, fallbackFrom)
          );
          await downloadInvoicePdf(payload);
          toast.success('Invoice PDF downloaded');
        } catch (error) {
          console.error('Invoice PDF export failed', error);
          toast.error(getApiErrorMessage(error));
        } finally {
          setPdfDownloadingId(null);
        }
      })();
      return;
    }

    if (action === 'delete') {
      if (!invoice?.id) return;
      setInvoiceConfirmFeedback(null);
      setPendingInvoiceConfirm({ variant: 'delete', invoice });
      return;
    }

    if (action === 'archive') {
      if (!invoice?.id) return;
      const unarchive = activeTab === 3; // Unarchive only inside Archived tab.
      setInvoiceConfirmFeedback(null);
      setPendingInvoiceConfirm({
        variant: unarchive ? 'unarchive' : 'archive',
        invoice,
      });
    }
  };

  return (
    <Box data-demo-id="invoicing-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 }, ...invoicingFont }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '30px',
          border: '1px solid #dbe6eb',
          bgcolor: '#fbfbfb',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #dbe6eb',
              bgcolor: '#fafcfe',
              px: { xs: 2, sm: 3 },
              py: 1.5,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', lineHeight: '20px', color: '#0f172a', fontWeight: 400 }}>
                Invoices and Billing
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3.5, lg: 4 }, pb: { xs: 3, sm: 4 }, pt: { xs: 2.3, sm: 2.8 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography
                sx={{
                  color: '#1D2B2D',
                  fontFamily: '"Inter", system-ui, sans-serif',
                  fontSize: '32px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: 'normal',
                }}
              >
                Invoices and Billing
              </Typography>
              <Typography
                sx={{
                  color: '#717182',
                  textAlign: 'center',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                  alignSelf: 'stretch',
                }}
              >
                Manage client invoices and track payments
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', alignSelf: 'center' }}>
              <TextField
                select
                value={invoiceDateBasis}
                onChange={(e) => setInvoiceDateBasis(e.target.value as InvoiceDateBasis)}
                size="small"
                sx={{
                  width: 134,
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: 600,
                    bgcolor: '#fbfcff',
                    color: '#2f3a4a',
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dde2f0' },
                }}
              >
                <MenuItem value="invoiceDate" sx={{ fontSize: '12px', fontWeight: 600 }}>
                  Invoice date
                </MenuItem>
                <MenuItem value="paidDate" sx={{ fontSize: '12px', fontWeight: 600 }}>
                  Paid date
                </MenuItem>
              </TextField>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outlined"
                    endIcon={
                      dateRange?.from ? (
                        <Box
                          component="span"
                          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setDateRange(undefined);
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <X size={14} color="#667085" />
                        </Box>
                      ) : (
                        <ChevronDown size={14} />
                      )
                    }
                    startIcon={
                      <Box
                        component="img"
                        src={appointmentCalendarFilterIcon}
                        alt=""
                        sx={{ width: 17, height: 17, display: 'block' }}
                      />
                    }
                    sx={{
                      minWidth: 42,
                      pl: 1.25,
                      pr: 0.75,
                      py: 0.4,
                      height: 36,
                      borderRadius: '8px',
                      borderColor: '#dde2f0',
                      color: '#2f3a4a',
                      textTransform: 'none',
                    }}
                  >
                    {dateRange?.from ? (
                      dateRange.to && dateRange.to.getTime() !== dateRange.from.getTime() ? (
                        `${format(dateRange.from, 'MMM d')} - ${format(dateRange.to, 'MMM d')}`
                      ) : (
                        format(dateRange.from, 'MMM d, yyyy')
                      )
                    ) : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={setDateRange}
                    numberOfMonths={1}
                  />
                </PopoverContent>
              </Popover>
              <Button
                data-demo-id="invoicing-create"
                variant="contained"
                disableElevation
                onClick={() => setIsCreateInvoiceOpen(true)}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#20aedc',
                  borderRadius: '10px',
                  px: '12px',
                  py: '8px',
                  fontSize: '14px',
                  lineHeight: '20px',
                  fontWeight: 500,
                  letterSpacing: '-0.15px',
                  '&:hover': { bgcolor: '#1a9bc6' },
                }}
              >
                Generate Invoice +
              </Button>
              <NotificationCenter buttonVariant="feature" />
            </Box>
          </Box>

          <Box sx={{ mt: 3.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: '14px' }}>
            <Paper elevation={0} sx={{ p: '12px', borderRadius: '14px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Box sx={{ p: '10px', borderRadius: '8px', bgcolor: '#e8f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box component="img" src={totalInvoicedIcon} alt="Total invoiced icon" sx={{ width: 24, height: 24 }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', lineHeight: 1.4 }}>
                <Typography sx={invoicingSummaryCaptionSx}>Total Invoiced</Typography>
                <Typography sx={invoicingSummaryAmountSx}>{formatInvoiceCurrencyAmount(summary.totalBilled, invoiceDisplayCurrency)}</Typography>
                <Typography sx={invoicingSummaryCaptionSx}>{invoiceSummaryDateCaption}</Typography>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: '12px', borderRadius: '14px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Box sx={{ p: '10px', borderRadius: '8px', bgcolor: 'rgba(0, 187, 167, 0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box component="img" src={amountCollectedIcon} alt="Amount collected icon" sx={{ width: 24, height: 24 }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', lineHeight: 1.4 }}>
                <Typography sx={invoicingSummaryCaptionSx}>Collected</Typography>
                <Typography sx={invoicingSummaryAmountSx}>{formatInvoiceCurrencyAmount(summary.totalPaid, invoiceDisplayCurrency)}</Typography>
                <Typography sx={invoicingSummaryCaptionSx}>{summary.paidCount} invoices paid</Typography>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: '12px', borderRadius: '14px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <Box sx={{ p: '10px', borderRadius: '8px', bgcolor: 'rgba(255, 162, 162, 0.17)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box component="img" src={overdueIcon} alt="Overdue icon" sx={{ width: 24, height: 24 }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', lineHeight: 1.4 }}>
                <Typography sx={invoicingSummaryCaptionSx}>Overdue</Typography>
                <Typography sx={invoicingSummaryAmountSx}>{summary.overdueCount}</Typography>
                <Typography sx={invoicingSummaryCaptionSx}>Past due date</Typography>
              </Box>
            </Paper>
          </Box>

          <Box
            sx={{
              mt: 3.5,
              borderBottom: '0.67px solid rgba(0,0,0,0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              '@media (min-width: 1240px)': {
                flexWrap: 'nowrap',
              },
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, newTab) => {
                setActiveTab(newTab);
                setInvoiceListPage(1);
              }}
              sx={{
                minHeight: 44,
                '& .MuiTabs-indicator': { backgroundColor: '#20aedc', height: '1.5px' },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 44,
                  px: 'clamp(16px, 1.2vw, 28px)', // Stays at ~16px around 1240px, grows slowly on larger screens
                  py: 1.5,
                  minWidth: 0,
                  color: '#717182',
                  fontSize: '16px',
                  lineHeight: '20px',
                  fontWeight: 400,
                },
                '& .Mui-selected': { color: '#20aedc', fontWeight: 500 },
              }}
            >
              {tabLabels.map((tab, tabIndex) => (
                <Tab
                  key={tab.label}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span>{tab.label}</span>
                      <Tag
                        variant="neutral"
                        size="xs"
                        className={`px-2 py-0.5 ${activeTab === tabIndex ? 'font-bold' : 'font-normal'}`}
                        sx={{
                          ...tabCountChipSx,
                          bgcolor: activeTab === tabIndex ? '#20aedc' : '#ececf0',
                          color: activeTab === tabIndex ? '#fff' : '#717182',
                        }}
                      >
                        {tab.count}
                      </Tag>
                    </Box>
                  }
                />
              ))}
            </Tabs>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', pb: 1 }}>
              <TextField
                select
                value={sortOrder === 'desc' ? 'newToOld' : 'oldToNew'}
                onChange={(e) => setSortOrder(e.target.value === 'oldToNew' ? 'asc' : 'desc')}
                size="small"
                sx={{
                  width: 110,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    height: 28,
                    fontSize: '12px',
                    lineHeight: '24px',
                    fontWeight: 500,
                    bgcolor: '#fbfcff',
                    borderColor: '#dde2f0',
                    color: 'rgba(0,0,0,0.8)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dde2f0' },
                }}
              >
                <MenuItem value="newToOld" sx={{ fontSize: '12px', fontWeight: 500 }}>
                  New to Old
                </MenuItem>
                <MenuItem value="oldToNew" sx={{ fontSize: '12px', fontWeight: 500 }}>
                  Old to New
                </MenuItem>
              </TextField>

              <SlashSearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search"
                sx={{
                  width: 228,
                  '& .MuiOutlinedInput-root': {
                    height: 40,
                    borderRadius: '16px',
                    bgcolor: 'rgba(0,0,0,0.04)',
                    fontSize: '14px',
                    lineHeight: '20px',
                    '& fieldset': { border: 'none' },
                    '&:hover fieldset': { border: 'none' },
                    '&.Mui-focused fieldset': { border: 'none' },
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 1px rgba(32, 174, 220, 0.35)',
                    },
                  },
                  '& .MuiInputBase-input': { fontSize: '14px' },
                  '& .MuiInputBase-input::placeholder': { color: 'rgba(0,0,0,0.2)', opacity: 1 },
                }}
              />
            </Box>
          </Box>

          <Paper elevation={0} sx={{ mt: 2.1, borderRadius: '16px', border: '1px solid #ebebeb', overflow: 'hidden' }}>
            <Box
              sx={{
                minHeight: 48.5,
                borderBottom: '1px solid #f2f2f2',
                px: { xs: 2.2, md: '20px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: invoiceCurrencyBadgeLabel.length === 1 ? 24 : 'auto',
                    minWidth: 24,
                    height: 24,
                    px: invoiceCurrencyBadgeLabel.length === 1 ? 0 : 0.7,
                    borderRadius: '999px',
                    bgcolor: '#d6eef7',
                    color: '#4db8df',
                    fontWeight: 700,
                    fontSize: invoiceCurrencyBadgeLabel.length === 12? '15px' : '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                  }}
                >
                  {invoiceCurrencyBadgeLabel}
                </Box>
                <Typography
                  sx={{
                    color: '#111',
                    fontWeight: 600,
                    fontSize: '13px',
                    lineHeight: '19.5px',
                    letterSpacing: '-0.0762px',
                  }}
                >
                  All Invoices
                </Typography>
              </Box>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: { xs: 0, md: 1020 } }}>
                <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: invoiceTableColumns, bgcolor: '#f3faff', borderBottom: '1px solid #f5f5f5' }}>
                  {['Invoice ID', 'Client', invoiceDateColumnLabel, 'Due Date', 'Amount', 'Status', 'Send Reminder', 'Action'].map((heading) => (
                    <Box key={heading} sx={{ ...invoiceTableCellSx, justifyContent: 'center', py: { md: 4.5 }, px: { md: heading === 'Invoice ID' ? 2 : undefined } }}>
                      <Typography
                        sx={{
                          color: '#1a1a1a',
                          fontWeight: 600,
                          fontSize: '12px',
                          lineHeight: '18px',
                          textAlign: 'center',
                        }}
                      >
                        {heading}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {isLoading ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: '#667085', fontSize: '14px' }}>Loading invoices...</Typography>
                  </Box>
                ) : invoices.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: '#667085', fontSize: '14px' }}>No invoices found.</Typography>
                  </Box>
                ) : (
                  invoices.map((inv, index) => {
                    const status = (inv.status || '').toLowerCase();
                    const isPaid = status === 'paid';
                    const statusValue = normalizeStatusForSelect(inv.status);
                    const busyStatus = statusUpdatingId === inv.id;
                    const canSendReminder = !isPaid;

                    return (
                      <Box
                        key={`${inv.id}-${index}`}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr 1fr', md: invoiceTableColumns },
                          borderBottom: index < invoices.length - 1 ? '1px solid #eceff3' : 'none',
                          bgcolor: '#fff',
                          px: { xs: 1.2, md: 0 },
                          py: { xs: 1.2, md: 0 },
                          gap: { xs: 0.4, md: 0 },
                        }}
                      >
                        <Box sx={{ ...invoiceTableCellSx, justifyContent: { xs: 'flex-start', md: 'center' }, py: { xs: 1.1, md: 4.15 } }}>
                          <Typography sx={{ display: { xs: 'inline', md: 'none' }, minWidth: 84, color: '#667085', fontSize: '12px', fontWeight: 600 }}>Invoice ID</Typography>
                          <Typography sx={{ ...invoiceBodyTextSx, textAlign: { xs: 'left', md: 'center' }, wordBreak: 'break-word' }}>{inv.invoiceNo || inv.id}</Typography>
                        </Box>
                        <Box sx={{ ...invoiceTableCellSx, justifyContent: { xs: 'flex-start', md: 'center' }, py: { xs: 1.1, md: 4.15 } }}>
                          <Typography sx={{ display: { xs: 'inline', md: 'none' }, minWidth: 84, color: '#667085', fontSize: '12px', fontWeight: 600 }}>Client</Typography>
                          <Typography sx={{ ...invoiceBodyTextSx, textAlign: { xs: 'left', md: 'center' } }}>
                            <ClientNameLink clientId={inv.clientId} className="text-[#1a1a1a] hover:text-[#1a1a1a]">
                              {getClientName(inv)}
                            </ClientNameLink>
                          </Typography>
                        </Box>
                        <Box sx={{ ...invoiceTableCellSx, justifyContent: { xs: 'flex-start', md: 'center' }, py: { xs: 1.1, md: 4.15 } }}>
                          <Typography sx={{ display: { xs: 'inline', md: 'none' }, minWidth: 84, color: '#667085', fontSize: '12px', fontWeight: 600 }}>{invoiceDateColumnLabel}</Typography>
                          <Typography sx={{ ...invoiceBodyTextSx, textAlign: { xs: 'left', md: 'center' } }}>{formatDate(getInvoiceBasisDate(inv) || undefined)}</Typography>
                        </Box>
                        <Box sx={{ ...invoiceTableCellSx, justifyContent: { xs: 'flex-start', md: 'center' }, py: { xs: 1.1, md: 4.15 } }}>
                          <Typography sx={{ display: { xs: 'inline', md: 'none' }, minWidth: 84, color: '#667085', fontSize: '12px', fontWeight: 600 }}>Due Date</Typography>
                          <Typography sx={{ ...invoiceBodyTextSx, textAlign: { xs: 'left', md: 'center' } }}>{formatDate(inv.dueDate)}</Typography>
                        </Box>
                        <Box sx={{ ...invoiceTableCellSx, justifyContent: { xs: 'flex-start', md: 'center' }, py: { xs: 1.1, md: 4.15 } }}>
                          <Typography sx={{ display: { xs: 'inline', md: 'none' }, minWidth: 84, color: '#667085', fontSize: '12px', fontWeight: 600 }}>Amount</Typography>
                          <Typography sx={{ ...invoiceBodyTextSx, textAlign: { xs: 'left', md: 'center' } }}>{formatInvoiceCurrencyAmount(inv.totalDue ?? inv.amount ?? 0, inv.currency || invoiceDisplayCurrency)}</Typography>
                        </Box>
                        <Box sx={{ ...invoiceTableCellSx, justifyContent: { xs: 'flex-start', md: 'center' }, py: { xs: 1.1, md: 4.15 } }}>
                          <Typography sx={{ display: { xs: 'inline', md: 'none' }, minWidth: 84, color: '#667085', fontSize: '12px', fontWeight: 600 }}>Status</Typography>
                          <FormControl size="small">
                            <Select<StatusSelectValue>
                              value={statusValue}
                              onChange={(e) => handleStatusChange(inv, e)}
                              IconComponent={KeyboardArrowDownIcon}
                              sx={{
                                minWidth: 120,
                                height: 32,
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d7dee7' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d7dee7' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#d7dee7' },
                                '&.Mui-focused': { boxShadow: 'none' },
                                '& .MuiSelect-select': { py: '6px', px: 1.25 },
                              }}
                              renderValue={(selected) => (
                                <Box component="span" sx={{ color: statusColor(selected), fontSize: '12px', fontWeight: 600, lineHeight: 1 }}>
                                  {busyStatus ? 'Updating...' : statusLabel(selected)}
                                </Box>
                              )}
                            >
                              <MenuItem value="due">Due</MenuItem>
                              <MenuItem value="overdue">Overdue</MenuItem>
                              <MenuItem value="paid">Paid</MenuItem>
                              <MenuItem value="discarded">Discarded</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Box sx={{ ...invoiceTableCellSx, justifyContent: { xs: 'flex-start', md: 'center' }, py: { xs: 1.1, md: 4.15 } }}>
                          <Typography sx={{ display: { xs: 'inline', md: 'none' }, minWidth: 84, color: '#667085', fontSize: '12px', fontWeight: 600 }}>Reminder</Typography>
                          {canSendReminder ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'center' }, gap: 1, minHeight: 36 }}>
                              <Button
                                variant="outlined"
                                endIcon={
                                  <Box
                                    component="span"
                                    sx={{
                                      display: 'inline-flex',
                                      width: 13,
                                      height: 13,
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      visibility: remindingInvoiceId === inv.id ? 'hidden' : 'visible',
                                    }}
                                  >
                                    <ArrowUpRight size={13} />
                                  </Box>
                                }
                                disabled={remindingInvoiceId === inv.id}
                                onClick={() => void handleSendReminder(inv.id)}
                                sx={{
                                  textTransform: 'none',
                                  borderColor: '#e5e7eb',
                                  borderWidth: '0.75px',
                                  color: '#4a5565',
                                  borderRadius: '10px',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  lineHeight: '15px',
                                  letterSpacing: '0.1172px',
                                  py: 0.75,
                                  px: 1.25,
                                  minWidth: 118,
                                  '&:hover': { borderColor: '#cad3de', bgcolor: '#f8fafc' },
                                  // MUI applies ~0.38 opacity to disabled buttons — restore full visibility for label
                                  '&.Mui-disabled': {
                                    opacity: 1,
                                    color: '#657082',
                                    WebkitTextFillColor: '#657082',
                                    borderColor: '#d7dee7',
                                    bgcolor: '#fafbfc',
                                    cursor: 'wait',
                                  },
                                }}
                              >
                                Send Reminder
                              </Button>
                              {remindingInvoiceId === inv.id ? (
                                <CircularProgress size={18} thickness={5} sx={{ color: '#57b7de', flexShrink: 0 }} />
                              ) : null}
                            </Box>
                          ) : (
                            <Typography sx={{ ...invoiceBodyTextSx, textAlign: 'center' }}>NA</Typography>
                          )}
                        </Box>
                        <Box sx={{ ...invoiceTableCellSx, justifyContent: { xs: 'flex-start', md: 'center' }, py: { xs: 1.1, md: 4.15 } }}>
                          <Typography sx={{ display: { xs: 'inline', md: 'none' }, minWidth: 84, color: '#667085', fontSize: '12px', fontWeight: 600 }}>Action</Typography>
                          <IconButton
                            size="small"
                            onClick={(event) => handleOpenActionsMenu(event, index)}
                            disabled={
                              deletingInvoiceId === inv.id ||
                              statusUpdatingId === inv.id ||
                              pdfDownloadingId === inv.id
                            }
                            sx={{ color: '#111', p: 0.6 }}
                            aria-label="Invoice actions"
                            aria-controls={isActionsMenuOpen ? 'invoice-actions-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={isActionsMenuOpen ? 'true' : undefined}
                          >
                            <EllipsisVertical size={20} />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
            {invoiceListTotalPages > 1 ? (
              <Box
                sx={{
                  py: 2.5,
                  display: 'flex',
                  justifyContent: 'center',
                  borderTop: '1px solid #f2f2f2',
                  bgcolor: '#fff',
                }}
              >
                <PaginationControls
                  currentPage={invoiceListPage}
                  totalPages={invoiceListTotalPages}
                  onPageChange={setInvoiceListPage}
                  pageSize={INVOICE_LIST_PAGE_SIZE}
                  isLoading={isLoading}
                  showSummary={false}
                />
              </Box>
            ) : null}
          </Paper>
        </Box>
      </Paper>

      <Menu
        id="invoice-actions-menu"
        anchorEl={actionsAnchorEl}
        open={isActionsMenuOpen}
        onClose={handleCloseActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.8,
              minWidth: 260,
              borderRadius: '14px',
              border: '1px solid #eceff3',
              bgcolor: '#fbfcff',
              boxShadow: '0px 16px 30px rgba(20, 24, 33, 0.12)',
              overflow: 'hidden',
              p: 0.5,
            },
          },
        }}
      >
        {invoiceActionItems.map((actionItem) => {
          const showUnarchive =
            actionItem.key === 'archive' &&
            activeTab === 3;
          const rowLabel = showUnarchive ? 'Unarchive' : actionItem.label;
          const rowIconAlt = showUnarchive ? 'Unarchive invoice' : actionItem.iconAlt;
          return (
          <MenuItem
            key={actionItem.key}
            onClick={() => handleInvoiceActionClick(actionItem.key)}
            sx={{
              borderRadius: '10px',
              py: 1.1,
              px: 1.1,
              gap: 1.05,
              '&:hover': { bgcolor: '#f0f4f8' },
            }}
          >
            {actionItem.key === 'download' ? (
              <Box
                aria-hidden
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: INVOICE_MENU_DOWNLOAD_ICON_COLOR,
                }}
              >
                <Download size={actionItem.iconSize} strokeWidth={2} />
              </Box>
            ) : (
              <Box
                component="img"
                src={actionItem.iconSrc}
                alt={rowIconAlt}
                sx={{ width: actionItem.iconSize, height: actionItem.iconSize, display: 'block', flexShrink: 0 }}
              />
            )}
            <ListItemText
              primary={rowLabel}
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#717989',
                lineHeight: 1.25,
              }}
            />
          </MenuItem>
          );
        })}
      </Menu>

      <CreateInvoiceFlowDialog
        open={isCreateInvoiceOpen}
        onClose={() => setIsCreateInvoiceOpen(false)}
        onInvoiceUpdated={() => void reloadInvoicesAndDashboard()}
      />
      <InvoiceDetailsDialog
        open={Boolean(viewInvoice)}
        invoice={viewInvoice}
        onClose={() => setViewInvoice(null)}
        onInvoiceUpdated={() => void reloadInvoicesAndDashboard()}
      />
      <InvoiceStatusConfirmDialog
        open={Boolean(pendingStatusChange)}
        targetStatus={pendingStatusChange?.next === 'paid' ? 'paid' : pendingStatusChange?.next === 'discarded' ? 'discarded' : null}
        isSubmitting={Boolean(statusUpdatingId)}
        feedback={invoiceStatusFeedback}
        onCancel={() => {
          setInvoiceStatusFeedback(null);
          setPendingStatusChange(null);
        }}
        onConfirm={() => {
          if (pendingStatusChange) {
            void handleConfirmStatusChange(pendingStatusChange.invoice, pendingStatusChange.next);
          }
        }}
      />
      <InvoiceConfirmDialog
        open={Boolean(pendingInvoiceConfirm)}
        variant={pendingInvoiceConfirm?.variant ?? 'delete'}
        isSubmitting={
          Boolean(pendingInvoiceConfirm) &&
          (deletingInvoiceId === pendingInvoiceConfirm.invoice.id || statusUpdatingId === pendingInvoiceConfirm.invoice.id)
        }
        feedback={invoiceConfirmFeedback}
        onCancel={() => {
          const p = pendingInvoiceConfirm;
          if (!p) return;
          if (deletingInvoiceId === p.invoice.id || statusUpdatingId === p.invoice.id) return;
          setInvoiceConfirmFeedback(null);
          setPendingInvoiceConfirm(null);
        }}
        onConfirm={() => void handleInvoiceConfirmDialogConfirm()}
      />
    </Box>
  );
};

export default InvoicingPage;
